import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { GardienBadge } from "@/components/GardienBadge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GARDIENS } from "@/data/gardiens";
import { api } from "@/lib/api";

const DEFAULTS = {
  patho_eyebrow: "Comprendre & accompagner",
  patho_title: "Les pathologies et les prises en charge non médicamenteuses",
  patho_subtitle: "Les approches non médicamenteuses complètent le soin en agissant sur le bien-être, les sens et le lien. Découvrez comment nos objets s'inscrivent dans ces démarches.",
  patho_1_name: "Maladie d'Alzheimer",
  patho_1_intro: "Une maladie neurodégénérative qui altère progressivement la mémoire, le langage et les repères.",
  patho_1_image: "https://images.unsplash.com/photo-1733512434374-c7fe34148c9a",
  patho_1_a1_t: "Thérapie de réminiscence",
  patho_1_a1_d: "Utiliser des objets, photos et parfums du passé pour raviver les souvenirs et l'identité. Nos boîtes à souvenirs et albums personnalisés soutiennent cette approche.",
  patho_1_a2_t: "Stimulation sensorielle",
  patho_1_a2_d: "Solliciter le toucher, l'ouïe et la vue pour maintenir l'éveil cognitif. Sets de manipulation et objets texturés apaisent et occupent les mains.",
  patho_1_a3_t: "Repères temporels",
  patho_1_a3_d: "Restaurer la notion du temps réduit l'anxiété et la désorientation. Horloges jour/nuit adaptées et supports visuels clairs y contribuent.",
  patho_2_name: "Troubles du spectre autistique",
  patho_2_intro: "Une manière différente de percevoir le monde, avec des besoins sensoriels et de prévisibilité spécifiques.",
  patho_2_image: "https://images.unsplash.com/photo-1634393654272-9f6b168356fd",
  patho_2_a1_t: "Intégration sensorielle",
  patho_2_a1_d: "Des objets lestés et texturés aident à réguler le système nerveux, procurant calme et sécurité. Nos plaids lestés et coussins tactiles sont conçus pour cela.",
  patho_2_a2_t: "Prévisibilité & structure",
  patho_2_a2_d: "Des supports visuels et routines matérialisées rassurent et réduisent le stress. Nous créons des kits adaptés au quotidien de chacun.",
  patho_2_a3_t: "Auto-régulation",
  patho_2_a3_d: "Fournir des outils d'apaisement permet de gérer les moments de surcharge sensorielle de façon autonome et bienveillante.",
  patho_3_name: "Maladies dégénératives & particuliers",
  patho_3_intro: "Parkinson, sclérose, perte d'autonomie — ou simplement le besoin de douceur et de lien.",
  patho_3_image: "https://images.unsplash.com/photo-1641514668935-86c738d0df20",
  patho_3_a1_t: "Maintien de la motricité",
  patho_3_a1_d: "Des objets à manipuler entretiennent la dextérité et la coordination, en douceur et sans contrainte.",
  patho_3_a2_t: "Lien social & émotionnel",
  patho_3_a2_d: "Les objets partagés créent des occasions d'échange entre la personne et ses proches ou soignants.",
  patho_3_a3_t: "Confort & bien-être",
  patho_3_a3_d: "Chaque création vise avant tout le confort, la dignité et le plaisir sensoriel de la personne.",
  benefits_title: "Les avantages de l'approche non médicamenteuse",
  benefit_1: "Sans effet secondaire médicamenteux",
  benefit_2: "Renforce le lien avec les proches",
  benefit_3: "Réduit l'anxiété et l'agitation",
  benefit_4: "Valorise l'histoire et l'identité",
  benefit_5: "S'adapte au rythme de chacun",
  benefit_6: "Complémentaire au suivi médical",
  patho_cta_label: "Découvrir les objets adaptés",
};

const Pathologies = () => {
  const [c, setC] = useState(DEFAULTS);
  const { hash } = useLocation();
  useEffect(() => { api.get("/content").then((r) => setC({ ...DEFAULTS, ...r.data })).catch(() => {}); }, []);
  // Défilement vers la section demandée (/pathologies#pathologie-N)
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
      return () => clearTimeout(t);
    }
  }, [hash]);

  const pathologies = [1, 2, 3].map((i) => ({
    n: `0${i}`,
    name: c[`patho_${i}_name`],
    intro: c[`patho_${i}_intro`],
    image: c[`patho_${i}_image`],
    approaches: [1, 2, 3].map((j) => ({ t: c[`patho_${i}_a${j}_t`], d: c[`patho_${i}_a${j}_d`] })),
  }));
  const benefits = [1, 2, 3, 4, 5, 6].map((i) => c[`benefit_${i}`]);

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">{c.patho_eyebrow}</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light max-w-4xl leading-tight">
            {c.patho_title}
          </h1>
          <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            {c.patho_subtitle}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="patho-banner">
            <img src="/affiche-pathologies.jpg" alt="Un chemin de lanternes et de fleurs où une main jeune tient tendrement une main âgée, veillées par une gardienne à la lanterne" className="w-full h-auto block" />
          </div>
        </Reveal>
        <div className="mt-8">
          <GardienBadge gardienId="emile" label="Gardien du parcours thérapeutique" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 space-y-24">
        {pathologies.map((p, i) => (
          <Reveal key={p.n}>
            <div id={`pathologie-${i + 1}`} className="grid lg:grid-cols-12 gap-10 scroll-mt-24">
              <div className="lg:col-span-4">
                <span className="font-serif-display text-8xl text-primary/20 leading-none">{p.n}</span>
                <h2 className="font-serif-display text-3xl sm:text-4xl mt-4">{p.name}</h2>
                <p className="mt-5 text-foreground/70 leading-relaxed">{p.intro}</p>
                {p.image && (
                  <img src={p.image} alt={p.name} data-testid={`patho-image-${i + 1}`}
                    className="mt-8 rounded-3xl w-full aspect-[4/3] object-cover border border-border/50" />
                )}
                {/* Gardiens associés à cette pathologie */}
                <div className="mt-8 bg-card rounded-2xl border border-warm/25 p-5" data-testid={`patho-gardiens-${i + 1}`}>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-warm mb-4">Les Gardiens de ce chemin</p>
                  <div className="flex flex-wrap gap-4">
                    {GARDIENS.filter((g) => g.pathoRef === i + 1).map((g) => (
                      <Link key={g.id} to={`/gardiens/${g.id}`} data-testid={`patho-gardien-${g.id}`}
                        className="group flex flex-col items-center w-16 hover:-translate-y-0.5 transition-transform">
                        <img src={g.image} alt={g.name} className="w-14 h-14 rounded-full object-cover object-top" style={{ boxShadow: `0 0 0 2px ${g.color}55` }} />
                        <span className="mt-1.5 text-[11px] text-center leading-tight text-foreground/75 group-hover:text-primary transition-colors">{g.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-8 lg:pt-4">
                <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
                  {p.approaches.map((a, j) => (
                    <AccordionItem key={j} value={`item-${j}`} data-testid={`approach-${i}-${j}`} className="border-border">
                      <AccordionTrigger className="font-serif-display text-xl sm:text-2xl hover:no-underline hover:text-primary text-left">{a.t}</AccordionTrigger>
                      <AccordionContent className="text-foreground/70 text-base leading-relaxed pb-6">{a.d}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-secondary/25 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-serif-display text-4xl sm:text-5xl font-light mb-14 max-w-2xl">{c.benefits_title}</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <div className="bg-card rounded-2xl border border-border/50 p-7 flex items-start gap-4">
                  <span className="font-serif-display text-2xl text-primary">✦</span>
                  <p className="text-foreground/80 leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="text-center mt-16">
              <Link to="/boutique" data-testid="pathologies-cta" className="inline-block rounded-full bg-primary text-primary-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">{c.patho_cta_label}</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Pathologies;
